import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, useColorScheme } from 'react-native';
import { Label, Paragraph, View, XStack, YStack } from 'tamagui';
// Lucide replaces the hand-rolled PlusIcon (identical mark; the old
// component's 2.5 stroke moves to the call site).
import { Plus as PlusIcon } from 'lucide-react-native';

import { CategoryChip } from '@/components/category-chip';
import { Text } from '@/components/text';
import { SolidSurface } from '@/components/glass-surface';
import { Input } from '@/components/input';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { TextArea } from '@/components/text-area';
import { useMyProfile } from '@/hooks/use-my-profile';
import { api, type OwnProfile, type Prompt } from '@/lib/api';
import { MAX_PROMPT_ANSWER_LEN, MAX_PROMPTS } from '@/lib/content-limits';
import { buildProfilePayload } from '@/lib/profile-draft';
import { queryKeys } from '@/lib/query-keys';
import { PROMPT_BANK, PROMPT_CATEGORIES, pickTwoPerCategory, type PromptCategory } from '@/lib/prompt-bank';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';
import { hit, slopFor } from '@/lib/hit-slop';

// Issue #238's profile-prompts split -- previously inline in
// profile/index.tsx (that screen's own doc comment tells the fuller
// story of why). Unlike Photos (profile-photos/index.tsx), Prompts
// never had per-action independent saves -- it only ever submitted as
// part of the same PATCH /me/profile call as Handle/Radius, since
// there's no blob to upload eagerly like photos have. Extracting it to
// its own screen means it NEEDS that independent save now (there's no
// shared "Save profile" button here to piggyback on), so this screen
// gets its own explicit "Save" action instead of Photos' per-action
// auto-save -- a real, deliberate difference, not an inconsistency:
// editing a freeform answer is real text-drafting (you don't want a
// PATCH fired on every keystroke the way Photos' discrete add/remove/
// reorder actions can afford), so an explicit commit point is the
// honest pattern here.
export function ProfilePrompts() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();
  const queryClient = useQueryClient();
  // Native semantic color, not a Tamagui token string -- react-native-svg's
  // stroke prop doesn't resolve Tamagui tokens (same reasoning every
  // other icon call site in this app documents, e.g.
  // location-radius-picker.tsx's own PlusIcon usage).
  const colorScheme = useColorScheme();
  const linkColor = colorScheme === 'dark' ? darkColors.textLink : lightColors.textLink;

  const ownProfile = useMyProfile();

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const hasPrefilled = useRef(false);

  useEffect(() => {
    if (hasPrefilled.current || !ownProfile.isSuccess) return;

    hasPrefilled.current = true;
    setPrompts(ownProfile.data.prompts);
  }, [ownProfile.isSuccess, ownProfile.data]);

  // Tucked-away, not shown alongside the bank by default -- same
  // founder call this held in profile/index.tsx before the move
  // (2026-09-15): freeform question-writing stays available (the
  // curated bank is the default/primary path, issue #216) but only
  // once the user explicitly asks for it.
  const [showFreeform, setShowFreeform] = useState(false);
  const [freeformQuestion, setFreeformQuestion] = useState('');
  const [freeformAnswer, setFreeformAnswer] = useState('');

  // Every field here except `prompts` comes straight from the
  // last-saved profile (ownProfile.data) -- same reasoning as
  // profile-photos/index.tsx's own buildPhotosPatch: this screen has
  // no handle/radius/location form state of its own to send, and
  // PATCH /me/profile has no partial-merge semantics.
  const save = useMutation({
    mutationFn: () => {
      if (!ownProfile.isSuccess) {
        throw new Error('Profile must exist before prompts can be edited.');
      }

      return api.updateProfile(
        buildProfilePayload(ownProfile.data, {
          // Same drop-blank-answer filter buildProfilePatch's own comment
          // documents: a picked bank question with no answer yet is a
          // draft row this screen lets you leave unfinished, not a
          // submission error. Screen-level filter; the module doesn't
          // second-guess it.
          prompts: prompts.filter((p) => p.answer.trim() !== ''),
        }),
      );
    },
    onSuccess: (profile: OwnProfile) => {
      queryClient.setQueryData(queryKeys.meProfile(), profile);
      router.back();
    },
  });

  const atCap = prompts.length >= MAX_PROMPTS;
  const usedQuestions = new Set(prompts.map((p) => p.question));
  // One category at a time (issue #255): 8 simultaneous suggestions is past
  // the working-memory limit at the moment a first-time profile-builder
  // hits this screen.
  const [activeCategory, setActiveCategory] = useState<PromptCategory>(PROMPT_CATEGORIES[0]);
  const [visibleEntries, setVisibleEntries] = useState(() => pickTwoPerCategory(PROMPT_BANK));
  const availableBankEntries = visibleEntries.filter((entry) => !usedQuestions.has(entry.question));

  function addFromBank(prompt: Prompt) {
    setPrompts([...prompts, prompt]);
  }

  function updateAnswer(index: number, answer: string) {
    setPrompts(prompts.map((p, i) => (i === index ? { ...p, answer } : p)));
  }

  function removePrompt(index: number) {
    setPrompts(prompts.filter((_, i) => i !== index));
  }

  function addFreeform() {
    if (freeformQuestion.trim() === '' || freeformAnswer.trim() === '') return;

    setPrompts([...prompts, { question: freeformQuestion.trim(), answer: freeformAnswer.trim() }]);
    setFreeformQuestion('');
    setFreeformAnswer('');
    setShowFreeform(false);
  }

  return (
    <ScreenContainer scrollUnderDock>
      {/* KeyboardAvoidingView + ScrollView + fixed-footer, matching
          profile/index.tsx's own established pattern exactly -- this
          screen shipped WITHOUT either (a real regression caught live,
          founder screenshot, 2026-09-18): a flat flex YStack has no
          scroll of its own, so content past the first viewport was
          unreachable, and the "Save" footer below -- which reads as a
          bottom-anchored bar because of its own borderTopWidth -- had
          nowhere to anchor to in a flex column with no scrolling
          sibling to push it down, so it rendered as an unanchored pill
          floating mid-content instead. No NestableScrollContainer here
          (unlike profile/index.tsx) -- nothing on this screen is a
          NestableDraggableFlatList, so a plain ScrollView is correct
          and lighter, matching preview/index.tsx's own precedent for a
          form screen with no drag-and-drop. */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <YStack flex={1}>
          <ScrollView
            style={{ flex: 1 }}
            {...dockScroll}
            contentContainerStyle={{ paddingTop: space[6], paddingBottom: space[6] + dockClearance, gap: space[5] }}
            keyboardShouldPersistTaps="handled"
          >
            <ScreenHeader
              title="Prompts"
              subtitle={`Pick up to ${MAX_PROMPTS} and share something real.`}
              onBack={() => router.back()}
            />

            {prompts.length > 0 && (
              <YStack gap="$3">
                {prompts.map((prompt, index) => {
                  const bankEntry = PROMPT_BANK.find((entry) => entry.question === prompt.question);
                  const isThisOrThat = bankEntry?.type === 'this-or-that';

                  return (
                    // Issue #294: chosen prompts now get a real selected-
                    // state treatment (interactiveSelectedBg/-Border,
                    // already in tokens, previously unused here) -- the
                    // whole reason the Suggestions section below needs a
                    // counter is that nothing on screen showed which
                    // prompts you'd actually picked; this is the other
                    // half of that fix; the ones you already added read
                    // as visibly "chosen," not identical plain text to
                    // everything else on the page.
                    <YStack
                      key={prompt.question}
                      gap="$2"
                      p="$3"
                      borderRadius="$md"
                      backgroundColor="$interactiveSelectedBg"
                      borderWidth={borderWidth.hairline}
                      borderColor="$interactiveSelectedBorder"
                    >
                      <XStack alignItems="flex-start" justifyContent="space-between" gap="$3">
                        <Text color="$color" fontWeight="$semibold" flex={1}>
                          {prompt.question}
                        </Text>
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel="Remove prompt"
                          {...hit({ vertical: slopFor(20) })}
                          onPress={() => removePrompt(index)}
                        >
                          <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
                            Remove
                          </Text>
                        </Pressable>
                      </XStack>
                      {isThisOrThat ? (
                        <Text color="$colorSecondary">{prompt.answer}</Text>
                      ) : (
                        <TextArea
                          value={prompt.answer}
                          onChangeText={(value) => updateAnswer(index, value)}
                          placeholder="Your answer..."
                          minHeight={60}
                          maxLength={MAX_PROMPT_ANSWER_LEN}
                        />
                      )}
                    </YStack>
                  );
                })}
              </YStack>
            )}

            {!atCap && (
              <YStack gap="$3">
                <XStack alignItems="center" justifyContent="space-between" gap="$3">
                  <XStack alignItems="center" gap="$2" flexShrink={1} flexWrap="wrap">
                    <Label color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                      Suggestions
                    </Label>
                    {/* Issue #294: Nielsen #1, Visibility of System
                        Status -- the header said "Pick up to 3" and
                        nothing anywhere showed how many you'd actually
                        picked. Real, live count, not a static label. */}
                    <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                      {prompts.length} of {MAX_PROMPTS} selected
                    </Text>
                  </XStack>
                  <Pressable
                    accessibilityRole="button"
                    {...hit({ vertical: slopFor(20) })}
                    onPress={() => setVisibleEntries(pickTwoPerCategory(PROMPT_BANK))}
                  >
                    <Text color="$colorLink" fontSize="$meta" lineHeight="$meta">
                      Randomize
                    </Text>
                  </Pressable>
                </XStack>

                <XStack gap="$2" flexWrap="wrap">
                  {PROMPT_CATEGORIES.map((category) => (
                    <CategoryChip
                      key={category}
                      label={category}
                      selected={category === activeCategory}
                      onPress={() => setActiveCategory(category)}
                    />
                  ))}
                </XStack>

                {PROMPT_CATEGORIES.map((category) => {
                  if (category !== activeCategory) return null;
                  const categoryEntries = availableBankEntries.filter((entry) => entry.category === category);
                  if (categoryEntries.length === 0) return null;

                  return (
                    <YStack key={category} gap="$2">
                      {/* Issue #294: was two incompatible row models in
                          one list -- centered single-line freeform
                          pills (no scannable left edge, a Nielsen #4
                          Consistency problem on its own), then a
                          totally different left-aligned question+chips
                          shape the moment a This-or-that entry showed
                          up. Both row types now share the SAME chrome:
                          a leading 16px icon, left-aligned label, and
                          (for this-or-that) the two option chips
                          right-aligned on the same row instead of
                          stacked below a taller card. */}
                      {categoryEntries.map((entry) =>
                        entry.type === 'freeform' ? (
                          <Pressable
                            key={entry.question}
                            accessibilityRole="button"
                            onPress={() => addFromBank({ question: entry.question, answer: '' })}
                          >
                            <SolidSurface style={{ padding: space[3] }}>
                              <XStack alignItems="center" gap="$2">
                                <PlusIcon size={16} strokeWidth={1.75} color={linkColor} />
                                <Text color="$colorLink" fontSize="$meta" lineHeight="$meta" textAlign="left" flex={1}>
                                  {entry.question}
                                </Text>
                              </XStack>
                            </SolidSurface>
                          </Pressable>
                        ) : (
                          <SolidSurface key={entry.question} style={{ padding: space[3] }}>
                            <XStack alignItems="center" justifyContent="space-between" gap="$2">
                              <Text
                                color="$color"
                                fontSize="$meta"
                                lineHeight="$meta"
                                fontWeight="$semibold"
                                textAlign="left"
                                flex={1}
                                numberOfLines={2}
                              >
                                {entry.question}
                              </Text>
                              <XStack gap="$2" flexShrink={0}>
                                <Pressable
                                  accessibilityRole="button"
                                  onPress={() => addFromBank({ question: entry.question, answer: entry.optionA })}
                                >
                                  <View
                                    borderWidth={borderWidth.hairline}
                                    borderColor="$borderColor"
                                    borderRadius="$sm"
                                    px="$2"
                                    py="$1"
                                  >
                                    <Text color="$colorLink" fontSize="$meta" lineHeight="$meta">
                                      {entry.optionA}
                                    </Text>
                                  </View>
                                </Pressable>
                                <Pressable
                                  accessibilityRole="button"
                                  onPress={() => addFromBank({ question: entry.question, answer: entry.optionB })}
                                >
                                  <View
                                    borderWidth={borderWidth.hairline}
                                    borderColor="$borderColor"
                                    borderRadius="$sm"
                                    px="$2"
                                    py="$1"
                                  >
                                    <Text color="$colorLink" fontSize="$meta" lineHeight="$meta">
                                      {entry.optionB}
                                    </Text>
                                  </View>
                                </Pressable>
                              </XStack>
                            </XStack>
                          </SolidSurface>
                        ),
                      )}
                    </YStack>
                  );
                })}
              </YStack>
            )}

            {!atCap &&
              (showFreeform ? (
                <SolidSurface style={{ padding: space[3] }}>
                  <YStack gap="$2">
                    <Input
                      value={freeformQuestion}
                      onChangeText={setFreeformQuestion}
                      placeholder="Write your own question..."
                    />
                    <TextArea
                      value={freeformAnswer}
                      onChangeText={setFreeformAnswer}
                      placeholder="Your answer..."
                      minHeight={60}
                      maxLength={MAX_PROMPT_ANSWER_LEN}
                    />
                    <XStack gap="$3">
                      <Pressable accessibilityRole="button" {...hit(8)} onPress={addFreeform}>
                        <Text color="$colorLink" fontSize="$meta" lineHeight="$meta">
                          Add
                        </Text>
                      </Pressable>
                      <Pressable
                        accessibilityRole="button"
                        {...hit(8)}
                        onPress={() => {
                          setFreeformQuestion('');
                          setFreeformAnswer('');
                          setShowFreeform(false);
                        }}
                      >
                        <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                          Cancel
                        </Text>
                      </Pressable>
                    </XStack>
                  </YStack>
                </SolidSurface>
              ) : (
                <Pressable
                  accessibilityRole="button"
                  {...hit({ vertical: slopFor(20) })}
                  onPress={() => setShowFreeform(true)}
                >
                  <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                    Write your own instead
                  </Text>
                </Pressable>
              ))}

            {/* Save moved INLINE, out of a fixed footer -- founder,
                2026-09-18: a persistent bottom bar "wasted space" and
                "persistently blocked the screen" rather than reading
                as the natural end of the form. Matches the same fix on
                profile/index.tsx (that file's own doc comment has the
                fuller reasoning) -- applied here too for consistency,
                since this screen was built with the identical fixed-
                footer pattern in the first place. */}
            <YStack gap="$3" pt="$6">
              {save.isError && (
                <Paragraph color="$destructive" textAlign="center">
                  {save.error.message || "Couldn't save your prompts. Check your connection and try again."}
                </Paragraph>
              )}
              <PrimaryButton
                disabled={save.isPending}
                onPress={() => {
                  if (!save.isPending) save.mutate();
                }}
              >
                {save.isPending ? <ActivityIndicator color="white" /> : 'Save'}
              </PrimaryButton>
            </YStack>
          </ScrollView>
        </YStack>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
